import { Box, Button, FormLabel } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import FEATURES from 'global/features';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import { useSaveReportSettings, ISaveReportSettings } from 'hooks/companySettings/useSaveReportSettings';
import { useFeatureList } from 'store/useFeatureStore';

import { intialReportData } from './schema';

const Report: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [formData, setFormData] = useState<any>(null);

  const featureList: any = useFeatureList();
  // const companyInfo = useCompanyInfo();
  const isCaliBreakPolicyOn = featureList[FEATURES.cali_break_policy];

  const { data, status, refetch } = useFetchSettings('report');
  const { status: saveReportStatus, mutateAsync } = useSaveReportSettings();

  // const isPremium =
  //   companyInfo?.plan?.toLowerCase() === PLANS.freeTrial || companyInfo?.plan?.toLowerCase() === PLANS.premium;

  useEffect(
    () => {
      if (status === 'success') {
        const formData: ISaveReportSettings = {
          ...data?.payload,
          show_timesheet_notes: data?.payload?.show_timesheet_notes ? data?.payload?.show_timesheet_notes : false,
          show_employee_id: data?.payload?.show_employee_id ? data?.payload?.show_employee_id : false,
          show_job_code: data?.payload?.show_job_code ? data?.payload?.show_job_code : false,
          show_mileage: data?.payload?.show_mileage ? data?.payload?.show_mileage : false,
          time_in_decimal: data?.payload?.time_in_decimal ? data?.payload?.time_in_decimal : false,
          show_approved_times: data?.payload?.show_approved_times ? data?.payload?.show_approved_times : false,
          include_inactive: data?.payload?.include_inactive ? data?.payload?.include_inactive : false,
          include_premium_pay: data?.payload?.include_premium_pay ? data?.payload?.include_premium_pay : false,
          // show_custom_fields: data?.payload?.show_custom_fields ? data?.payload?.show_custom_fields : false,
        };

        setFormData(formData);
      }
    },
    // eslint-disable-next-line
    [status]
  );

  const handleToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    setFieldValue(controlName, e.target.checked);
  };

  const handleFormSubmit = async (values: typeof intialReportData) => {
    const postData: ISaveReportSettings = {
      show_timesheet_notes: values.show_timesheet_notes,
      show_employee_id: values.show_employee_id,
      show_job_code: values.show_job_code,
      show_mileage: values.show_mileage,
      time_in_decimal: values.time_in_decimal,
      show_approved_times: values.show_approved_times,
      include_inactive: values.include_inactive,
      include_premium_pay: values.include_premium_pay,
      // show_custom_fields: values.show_custom_fields,
    };
    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading') return <LogoLoader />;

  return (
    <>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton={true}> {t('companySettings:reportsSettings')}</Title>
      </Box>
      <Box mt="26px">
        <Formik initialValues={formData || intialReportData} enableReinitialize onSubmit={handleFormSubmit}>
          {({ setFieldValue }) => (
            <Form noValidate>
              <Box display="flex" flexDirection="column" maxWidth="390px">
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showTimesheetNotesOnReports')}</FormLabel>
                  <FormikSwitch
                    name="show_timesheet_notes"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_timesheet_notes')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showUserIdOnReports')}</FormLabel>
                  <FormikSwitch
                    name="show_employee_id"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_employee_id')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showJobCodeOnReports')}</FormLabel>
                  <FormikSwitch
                    name="show_job_code"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_job_code')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showMileageOnReports')}</FormLabel>
                  <FormikSwitch
                    name="show_mileage"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_mileage')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showTimeInDecimalFormat')}</FormLabel>
                  <FormikSwitch
                    name="time_in_decimal"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'time_in_decimal')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showOnlyApproveTimeMileage')}</FormLabel>
                  <FormikSwitch
                    name="show_approved_times"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_approved_times')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:includeInactiveUsers')}</FormLabel>
                  <FormikSwitch
                    name="include_inactive"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'include_inactive')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:includePremiumPay')}</FormLabel>
                  <FormikSwitch
                    name="include_premium_pay"
                    switchProps={{
                      isDisabled: !isCaliBreakPolicyOn,
                    }}
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'include_premium_pay')}
                  ></FormikSwitch>
                </Box>
                {/* <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showCustomFields')}</FormLabel>
                  <FormikSwitch
                    name="show_custom_fields"
                    switchProps={{
                      isDisabled: !isPremium,
                    }}
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_custom_fields')}
                  ></FormikSwitch>
                </Box> */}
              </Box>

              <Button type="submit" colorScheme="secondary" isLoading={saveReportStatus === 'loading'} minWidth="100px">
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};
export default Report;
